/**
 * src/lib/api/quotes.js — frontend client for the native quote engine.
 * Thin wrappers over apiFetch -> /api/data?action=quotes.*
 * (cotiza merge, Milestone 1). Mutations return { success, ... } | { success:false, error }.
 */

import { apiFetch } from './client';
import { putToPresignedUrl } from './presignedPut';
import { makeEntityCache } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';

const D = (action) => `/api/data?action=${action}`;

/**
 * Per-quote cache key so each quote keeps its own offline copy of quote+lines+sections.
 * Best-effort — a cache miss/error never degrades the live result.
 * Mirrors the activitiesCache + fetchLeadThread pattern (TICKET-265).
 */
function quoteCache(quoteId) {
  return makeEntityCache('quote::' + quoteId);
}

export async function listQuotes({ deal_id, vessel_id, project_id, status, all } = {}) {
  const params = new URLSearchParams();
  if (deal_id) params.set('deal_id', deal_id);
  if (project_id) params.set('project_id', project_id);
  if (vessel_id) params.set('vessel_id', vessel_id);
  if (status) params.set('status', status);
  if (all) params.set('all', 'true');
  const qs = params.toString();
  const r = await apiFetch(`${D('quotes.list')}${qs ? `&${qs}` : ''}`);
  return r?.quotes || [];
}

export async function getQuote(id) {
  if (!id) return null;
  // TICKET-262 Phase 2: cache quote+lines+sections per quote so offline reopen works.
  // Mirrors fetchLeadThread — reconcile writes the cache online, hydrates it offline,
  // and the stale-empty guard keeps a good cache from being blanked by a transient empty fetch.
  const online = getOnline();
  try {
    const r = await apiFetch(`${D('quotes.get')}&id=${encodeURIComponent(id)}`);
    if (!r || !r.quote) {
      // TICKET-610: distinguish explicit auth denial (403/404) from transient failures.
      // If the server actively denied access (status 403/404), do NOT fall back to cache
      // even if one exists — that would bypass the authorization control.
      // Only fall back on transient failures: offline (null), timeout, network error.
      if (r?.status === 403 || r?.status === 404) {
        // Explicit denial: server refused to serve this quote to the current user.
        // Return null, never the cache.
        return null;
      }
      // Transient/offline failure: try offline cache
      const cached = await quoteCache(id).read()
        .catch(() => { /* spine-emit-ok: offline-cache fallback read; apiFetch already emitted the fetch failure (TICKET-262) */ return []; });
      if (cached.length && cached[0].quote) {
        return { quote: cached[0].quote, lines: cached[0].lines || [], sections: cached[0].sections || [] };
      }
      return null;
    }
    // Wrap the full response with an id field at the top level (required by idbReplaceDomain)
    // so it survives the entityCache filter. Store as a single-item array for reconcile.
    const cachePayload = { id, quote: r.quote, lines: r.lines || [], sections: r.sections || [] };
    const { rows } = await quoteCache(id)
      .reconcile({ fetched: [cachePayload], online })
      .catch(() => { /* spine-emit-ok: cache reconcile is best-effort; the fresh payload is already in hand (TICKET-262) */ return { rows: [cachePayload] }; });
    // Return the cached version (respects stale-empty guard when offline).
    const stored = rows[0] || cachePayload;
    return { quote: stored.quote, lines: stored.lines || [], sections: stored.sections || [] };
  } catch (e) {
    // On a read error, fall back to the offline cache so reopening the quote
    // still works with the last-known data (best-effort).
    // spine-emit-ok: apiFetch is the instrumented choke point and already emitted the failure; this catch only serves the offline cache (TICKET-262)
    // Note: with TICKET-610's fix, apiFetch no longer throws on GET errors (it returns
    // an error object), so this catch is now only for unexpected exceptions during
    // the cache read/reconcile operations, not for authorization denials.
    const cached = await quoteCache(id).read()
      .catch(() => { /* spine-emit-ok: offline-cache fallback read, same benign case as the outer catch */ return []; });
    if (cached.length && cached[0].quote) {
      return { quote: cached[0].quote, lines: cached[0].lines || [], sections: cached[0].sections || [] };
    }
    return null;
  }
}

// TICKET-720 — the quote builder fetched the FULL product catalog on every quote
// open. The catalog changes only through createQuoteProduct, so a short TTL +
// create-invalidation is exact; a failed/empty fetch is never cached (retry next call).
const QUOTE_PRODUCTS_TTL_MS = 5 * 60 * 1000;
let quoteProductsCache = null; // { at, products }

export function __clearQuoteProductsCache() { quoteProductsCache = null; }

export async function listQuoteProducts() {
  if (quoteProductsCache && (Date.now() - quoteProductsCache.at) < QUOTE_PRODUCTS_TTL_MS) {
    return quoteProductsCache.products;
  }
  const r = await apiFetch(D('quotes.products'));
  const products = r?.products || [];
  if (Array.isArray(r?.products)) quoteProductsCache = { at: Date.now(), products };
  return products;
}

// WS4 Silvana gap #9 — create a NEW catalogue product from the line picker.
// Returns the picker-shaped product row (or null on failure).
export async function createQuoteProduct(body) {
  const r = await apiFetch(D('quotes.create_product'), { method: 'POST', body: JSON.stringify(body) });
  if (r?.product) __clearQuoteProductsCache();
  return r?.product || null;
}

// METERED — AI description for a new product (name + key points → EN/ES copy).
// Routes to /api/ai (kill-switch gated). Returns { description, description_es }.
export async function generateProductDescription({ name, category, bullets, price_unit }) {
  return apiFetch('/api/ai', {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.product_description', name, category, bullets, price_unit }),
  });
}

export function createQuote(body) {
  return apiFetch(D('quotes.create'), { method: 'POST', body: JSON.stringify(body) });
}

export function updateQuote(body) {
  return apiFetch(D('quotes.update'), { method: 'POST', body: JSON.stringify(body) });
}

export function saveQuoteLines(quote_id, lines, opts = {}) {
  // 30s budget: a bloated quote (40+ lines) recomputes+persists per line server-side
  // and blew the default fetch timeout — the delete/save then failed SILENTLY
  // (CEO UAT 2026-07-08). The hook surfaces the error if even this is exceeded.
  // TICKET-941: known_line_ids = the persisted line ids the caller has SEEN; the
  // server then deletes only those absent from `lines`. Omitted = legacy replace-all.
  // configure_seen_at = the quote's last_configure_at as the caller loaded it; a newer
  // customer configure then keeps the buyer's is_selected / adjustable qty.
  const body = { quote_id, lines };
  if (Array.isArray(opts?.known_line_ids)) body.known_line_ids = opts.known_line_ids;
  if (opts && Object.prototype.hasOwnProperty.call(opts, 'configure_seen_at')) body.configure_seen_at = opts.configure_seen_at;
  return apiFetch(D('quotes.save_lines'), { method: 'POST', body: JSON.stringify(body), timeout: 30000 });
}

// AI line-description polish (CEO 2026-07-01): returns { proposals:[{line_id,before,after}] }
// grounded in the company context, in the quote's language. `line_ids` omitted = all
// lines (the one-sweep pass); `notes` = extra bullets/corrections to fold in. Returns
// proposals only — the caller persists accepted ones via the normal line patch.
// `language`: 'en'|'es'|'fr' forces the polish output language; 'auto'/omitted = the
// quote's language ("same as received"). CEO 2026-07-24.
// `format`: omitted/'structured' = scannable layout (lead line + bullets + spacing),
// the DEFAULT since CEO 2026-09-22; 'plain' is the explicit opt-out to flowing prose.
export function polishQuoteLines({ quote_id, line_ids, notes, language, format } = {}) {
  return apiFetch(D('quotes.polish_lines'), { method: 'POST', body: JSON.stringify({ quote_id, line_ids, notes, language, format }) });
}

// AI polish for the quote's free-text fields (CEO 2026-07-24): personalized message
// + additional terms. Same engine/guardrails + language picker as polishQuoteLines.
// `kind` = 'message' | 'terms'; `language` = 'en'|'es'|'fr'|'auto'. Returns
// { proposal:{before,after}|null } — the caller persists via quotes.update.
export function polishQuoteText({ quote_id, kind, text, notes, language, format } = {}) {
  return apiFetch(D('quotes.polish_text'), { method: 'POST', body: JSON.stringify({ quote_id, kind, text, notes, language, format }) });
}

// TICKET-1379: draft the proposal intro (TICKET-1378 action). Returns
// { success, text, warnings, attempts, language } | { success:false, killed:true } |
// { success:false, status, code } (503 intro_not_activated, 429 intro_rate_limited).
// Never writes the quote; the UI saves on Accept. noRetry: a paid LLM call must not
// run twice because the first attempt 5xx'd.
export function generateQuoteIntro({ quote_id, instruction, language } = {}) {
  return apiFetch(D('quotes.generate_intro'), { method: 'POST', noRetry: true, body: JSON.stringify({ quote_id, instruction, language }) });
}

// TICKET-933 Slice C: On-demand translation. Translates quote content (lines, sections,
// bundles, quote metadata) into the target language. Returns { success, translated_rows,
// fields, failed_rows, skipped_fresh } | { success:false, killed:true } on kill-switch.
export function translateQuote(quote_id, lang) {
  return apiFetch(D('quotes.translate'), { method: 'POST', body: JSON.stringify({ quote_id, lang }) });
}

// TICKET-294: send the quote PDF by email from inside the app via the server Gmail
// path. Server resolves + attaches the PDF (signed contract if signed, else the live
// proposal). 30s budget: a re-render can take several seconds. Returns { success,
// sent, reason? } — sent:false with a reason on gmail-unconfigured / send failure.
export function sendComposedQuoteEmail({ quote_id, to, subject, body } = {}) {
  return apiFetch(D('quotes.send_composed_email'), {
    method: 'POST', body: JSON.stringify({ quote_id, to, subject, body }), timeout: 30000,
  });
}

// Reopen a quote the customer has already opened: mints a fresh public_token (the
// old signing link dies), bumps quote_version, and resets open-tracking + expiry
// so the corrected version goes out on resend. Server refuses a signed quote.
export function reopenQuoteForEdit(id) {
  return apiFetch(D('quotes.reopen_for_edit'), { method: 'POST', body: JSON.stringify({ id }) });
}

// ── Line-lock (S6 → TICKET-942). locked:true = RELEASE: freezes the line set (save_lines
// then 409s) AND the revision letter (the same letter when nothing changed) →
// { success, lines_locked_at, revision:{ revision_label, reused, … } }. locked:false
// on a released quote starts the NEXT version (quote_version+1, same link — TICKET-946) →
// { success, lines_locked_at:null, reopened:true, next_revision_label, public_token }.
export function setQuoteLinesLock(id, locked) {
  return apiFetch(D('quotes.set_lines_lock'), { method: 'POST', body: JSON.stringify({ id, locked: !!locked }) });
}

// TICKET-942: the copy-link / share trigger. via = copy_link | share_email |
// share_whatsapp. Locks + freezes the letter that goes out (idempotent: copying the
// same unchanged quote twice is ONE letter). Returns { success, revision, lines_locked_at }.
export function releaseQuote(id, via = 'copy_link') {
  return apiFetch(D('quotes.release'), { method: 'POST', body: JSON.stringify({ quote_id: id, via }) });
}

// ── Linked contacts (TICKET-QUOTE-LINKING) — replace the quote's contact set.
// contacts: [{ contact_id, role?, is_primary? }]; role ∈ owner|captain|agency|
// billing|recipient. A quote can go to the owner AND an agency (e.g. BWA).
// Returns { success, contacts:[{ id, contact_id, role, is_primary, first_name,
// last_name, company_name, email }] } | { success:false, error }.
export function setQuoteContacts(quote_id, contacts) {
  return apiFetch(D('quotes.set_contacts'), { method: 'POST', body: JSON.stringify({ quote_id, contacts }) });
}

// ── Sections (Phase 2) ──────────────────────────────────────────────────────
// sections: [{ id?, name, sort_order, show_total_only }]. Returns { success, sections }.
export function saveQuoteSections(quote_id, sections) {
  return apiFetch(D('quotes.save_sections'), { method: 'POST', body: JSON.stringify({ quote_id, sections }) });
}

// ── Templates (Phase 2) ─────────────────────────────────────────────────────
export async function listQuoteTemplates() {
  const r = await apiFetch(D('quotes.list_templates'));
  return r?.templates || [];
}
// body: { name, category?, language?, description?, is_shared?, from_quote_id }.
export function saveQuoteTemplate(body) {
  return apiFetch(D('quotes.save_template'), { method: 'POST', body: JSON.stringify(body) });
}
// body: { template_id, deal_id|vessel_id, contact_id?, client_name?, boat_name? }.
export function createQuoteFromTemplate(body) {
  return apiFetch(D('quotes.create_from_template'), { method: 'POST', body: JSON.stringify(body) });
}

// ── Review gates (Phase 4) ──────────────────────────────────────────────────
// Sales: draft → pending_review.
export function submitQuoteReview(id) {
  return apiFetch(D('quotes.submit_review'), { method: 'POST', body: JSON.stringify({ id }) });
}
// CEO 2026-10-12: the PMO chose "Share anyway" on a quote the CEO has not approved —
// the server pushes the CEO (once per quote per 30 min) and logs the override.
export function notifyQuoteShareOverride(id) {
  return apiFetch(D('quotes.share_override'), { method: 'POST', body: JSON.stringify({ id }) });
}
// CEO/PMO: pending_review → approved_for_send | rejected. decision: 'approve'|'reject'.
export function approveQuoteReview(id, decision, reason) {
  return apiFetch(D('quotes.approve_review'), { method: 'POST', body: JSON.stringify({ id, decision, reason }) });
}

// ── Approval rules admin (CT-4 Slice 2, approver-gated server-side) ──────────
// GET → { success, rules:[{id,name,condition,approver_role,tier_order,active}],
//         fields, ops, roles }. save = create (no id) | update (id). archive =
//         soft-archive by id.
export function listApprovalRules() {
  return apiFetch(D('quotes.list_approval_rules'));
}
export function saveApprovalRule(rule) {
  return apiFetch(D('quotes.save_approval_rule'), { method: 'POST', body: JSON.stringify(rule) });
}
export function archiveApprovalRule(id) {
  return apiFetch(D('quotes.archive_approval_rule'), { method: 'POST', body: JSON.stringify({ id }) });
}

// ── Corrections learning log (Phase 4) ──────────────────────────────────────
// body: { quote_id, line_id?, field_name, engine_suggested, user_applied, reason?, override_category? }.
export function logQuoteCorrection(body) {
  return apiFetch(D('quotes.log_correction'), { method: 'POST', body: JSON.stringify(body) });
}

// signature (optional): { signature_name, signature_data } — captured on accept (S2).
export function acceptQuote(id, signature = {}) {
  return apiFetch(D('quotes.accept'), { method: 'POST', body: JSON.stringify({ id, ...signature }) });
}

export function archiveQuote(id) {
  return apiFetch(D('quotes.archive'), { method: 'POST', body: JSON.stringify({ id }) });
}

// TICKET-876: copy this quote (sections + lines + contacts) into a fresh unsent
// draft, so a pruned subset can be sent without deleting lines from the original.
export function duplicateQuote(id, opts = {}) {
  // TICKET-1518: opts.target_deal_id copies the quote INTO another deal ("start from a similar quote").
  return apiFetch(D('quotes.duplicate'), { method: 'POST', body: JSON.stringify({ id, ...(opts.target_deal_id ? { target_deal_id: opts.target_deal_id } : {}) }) });
}

// TICKET-239 Slice 6: freeze the current proposal as an immutable Rev A/B/C snapshot.
export function issueQuoteRevision(id) {
  return apiFetch(D('quotes.issue_revision'), { method: 'POST', body: JSON.stringify({ quote_id: id }) });
}

// UAT 2026-07-08 #13: reopen a SIGNED order into the next working revision
// (the signed contract stays frozen as an immutable order revision).
export function reopenSignedQuote(id) {
  return apiFetch(D('quotes.reopen_signed'), { method: 'POST', body: JSON.stringify({ quote_id: id }) });
}

// TICKET-239 Slice 6: the immutable issued-revision history (read-only).
export async function listQuoteRevisions(quote_id) {
  const r = await apiFetch(`${D('quotes.list_revisions')}&quote_id=${encodeURIComponent(quote_id)}`);
  return Array.isArray(r?.revisions) ? r.revisions : [];
}

// TICKET-942: the history PLUS whether the live offer still equals the last released
// letter → { revisions, working: { last_released_label, differs } | null }. Lets a
// reopened-but-unedited quote read "Rev A · reopened" instead of promising a Rev B.
export async function getQuoteRevisionState(quote_id) {
  const r = await apiFetch(`${D('quotes.list_revisions')}&quote_id=${encodeURIComponent(quote_id)}&include_state=1`);
  return {
    revisions: Array.isArray(r?.revisions) ? r.revisions : [],
    working: r?.working && typeof r.working === 'object' ? r.working : null,
  };
}

// ── Audio scope-generator (Phase 3) ────────────────────────────────────────
// Returns { success, quote_id, scope_run_id, lines, open_questions, confidence }
// | { success:false, error }. Pass EITHER { transcript } OR { audio (base64),
// mime_type }; optionally link with { deal_id, vessel_id, contact_id, language }.
export function generateQuoteScope(body) {
  // Audio transcription + extraction can take well past the default 15s.
  return apiFetch(D('quotes.scope_generate'), {
    method: 'POST',
    body: JSON.stringify(body),
    timeout: 120000,
  });
}

// Commit the reviewed scope: approve lines + record open-question answers.
// body: { scope_run_id | quote_id, accepted_lines: [id...], answers }.
export function applyQuoteScope(body) {
  return apiFetch(D('quotes.scope_apply'), { method: 'POST', body: JSON.stringify(body) });
}

// ── Generate from brain (Phase 215a) — a priced draft from existing capture,
// NO re-recording. body: { deal_id | project_id | vessel_id, bundle_ids?, language? }.
// Returns { success, quote_id, scope_run_id, sections, lines, open_questions, confidence }
// | { success:false, empty:true } when there is no brain to quote from yet.
export function generateQuoteFromBrain(body) {
  // Brain read + RAG + classification can take well past the default 15s.
  return apiFetch(D('quotes.generate_from_brain'), { method: 'POST', body: JSON.stringify(body), timeout: 120000 });
}

// ── Sources -> proposed bundles (TICKET-QB-SOURCES) — "build the bundle items
// first". Reads brain chips + recording summaries + the deal note and classifies
// them into PROPOSED bundles for review (no persist). Pass `answers` (the ask-back
// Q&A, text or voice-transcribed) to refine + reprocess. body: { deal_id |
// project_id | vessel_id, answers?, language? }. Returns
// { success, bundles, open_questions, confidence, source_count }.
export function generateBundlesFromSources(body) {
  // Brain read + recordings + Gemini classification can exceed the default 15s.
  return apiFetch(D('quotes.generate_bundles_from_sources'), { method: 'POST', body: JSON.stringify(body), timeout: 120000 });
}

// Persist the reviewed proposed bundles as project_bundles. body: { deal_id |
// project_id, vessel_id?, bundles: [{title, description, product_type, origin,
// priority_order, confidence}] }. Returns { success, bundles, bundle_ids }.
// After this, createQuote({ ..., seed_bundles:true }) (#384) prepares the quote.
export function createBundlesFromProposal(body) {
  return apiFetch(D('quotes.create_bundles_from_proposal'), { method: 'POST', body: JSON.stringify(body) });
}

// S2 provenance: per-line sources for a quote. Returns
// [{ line_id, bundle_id, sources:[{ kind, ref, label, document_id? }] }] | [].
export async function getQuoteLineSources(quoteId) {
  const r = await apiFetch(`${D('quotes.line_sources')}&quote_id=${encodeURIComponent(quoteId)}`);
  return Array.isArray(r?.line_sources) ? r.line_sources : [];
}

// ── Section templates (WS1-S4) ───────────────────────────────────────────────
// GET quotes.list_section_templates — returns { templates } | [].
// section_type: 'cover' | 'about' | 'body' | 'terms'; language: 'en' | 'es'.
export async function listSectionTemplates({ section_type, language, project_type_id, scope, include_drafts } = {}) {
  const params = new URLSearchParams();
  if (section_type) params.set('section_type', section_type);
  if (language) params.set('language', language);
  if (project_type_id) params.set('project_type_id', project_type_id);
  if (scope) params.set('scope', scope);              // 'global' → project_type_id IS NULL only
  if (include_drafts) params.set('include_drafts', '1'); // Settings library: show unpublished drafts
  const qs = params.toString();
  const r = await apiFetch(`${D('quotes.list_section_templates')}${qs ? `&${qs}` : ''}`);
  if (r === null) throw new Error('list_section_templates_failed');
  return r?.templates || [];
}

// POST quotes.save_section_template — { section_type, language, name, content }.
export function saveSectionTemplate(body) {
  return apiFetch(D('quotes.save_section_template'), { method: 'POST', body: JSON.stringify(body) });
}

// POST quotes.apply_section_template — { quote_id, section_type, content }.
// Merges content into quotes.section_overrides for that section type.
export function applySectionTemplate(body) {
  return apiFetch(D('quotes.apply_section_template'), { method: 'POST', body: JSON.stringify(body) });
}

// POST quotes.set_section_default — { section_type, language, content, project_type_id? }.
// Makes content the new ORG default (append-only: archives the prior default → history).
export function setSectionDefault(body) {
  return apiFetch(D('quotes.set_section_default'), { method: 'POST', body: JSON.stringify(body) });
}

// POST quotes.publish_section_template — { id, published } toggle a preset's publish state.
// Published presets appear in the builder Load list; drafts are Settings-library-only.
export function publishSectionTemplate(body) {
  return apiFetch(D('quotes.publish_section_template'), { method: 'POST', body: JSON.stringify(body) });
}

// GET quotes.list_section_default_history — { section_type, language } → { versions: [...] } | [].
// Timeline of a section's default-template versions (archived + live), newest first.
export async function listSectionDefaultHistory({ section_type, language } = {}) {
  const params = new URLSearchParams();
  if (section_type) params.set('section_type', section_type);
  if (language) params.set('language', language);
  const qs = params.toString();
  const r = await apiFetch(`${D('quotes.list_section_default_history')}${qs ? `&${qs}` : ''}`);
  return r?.versions || [];
}

// ── Template assets (TICKET-281 #3 — images + PDF datasheets on Hetzner MinIO) ───────
// GET quotes.list_template_assets — { section_template_id?, kind?, product_family?, language? } → [].
export async function listTemplateAssets({ section_template_id, kind, product_family, language } = {}) {
  const params = new URLSearchParams();
  if (section_template_id) params.set('section_template_id', section_template_id);
  if (kind) params.set('kind', kind);
  if (product_family) params.set('product_family', product_family);
  if (language) params.set('language', language);
  const qs = params.toString();
  const r = await apiFetch(`${D('quotes.list_template_assets')}${qs ? `&${qs}` : ''}`);
  return r?.assets || [];
}

// POST quotes.archive_template_asset — { id } soft-archive (never hard-delete).
export function archiveTemplateAsset(id) {
  return apiFetch(D('quotes.archive_template_asset'), { method: 'POST', body: JSON.stringify({ id }) });
}

// GET quotes.template_asset_download_url — { id } → { download_url, kind, mime_type, title } | null.
// Short-TTL presigned GET for library preview / builder-side hero+datasheet render.
export async function templateAssetDownloadUrl(id) {
  if (!id) return null;
  return apiFetch(`${D('quotes.template_asset_download_url')}&id=${encodeURIComponent(id)}`);
}

/**
 * Upload one image/PDF to MinIO and persist its metadata row. Three steps, NO silent
 * success: (1) presign a PUT, (2) PUT the bytes straight to MinIO (throws on non-2xx —
 * the object never lands, so we never save a dangling row), (3) save the metadata row.
 * Resolves to the saved asset ONLY after the row persists. onProgress(0..1) is called
 * for the PUT phase. Throws with a readable message on any failure so the UI shows it.
 */
export async function uploadTemplateAsset(file, meta = {}, onProgress) {
  if (!file) throw new Error('No file selected');
  const kind = meta.kind || (file.type === 'application/pdf' ? 'pdf' : 'image');
  const presign = await apiFetch(D('quotes.template_asset_upload_url'), {
    method: 'POST',
    body: JSON.stringify({ kind, mime_type: file.type, file_size_bytes: file.size, file_name: file.name }),
  });
  if (!presign?.url || !presign?.storage_key) throw new Error(presign?.error || 'Could not get an upload URL');

  await putWithProgress(presign.url, file, onProgress);

  const saved = await apiFetch(D('quotes.save_template_asset'), {
    method: 'POST',
    body: JSON.stringify({
      storage_key: presign.storage_key, kind, mime_type: file.type, file_size_bytes: file.size,
      title: meta.title || file.name, caption: meta.caption || null, alt_text: meta.alt_text || null,
      product_family: meta.product_family || null, language: meta.language || null,
      section_template_id: meta.section_template_id || null,
    }),
  });
  if (!saved?.success) throw new Error(saved?.error || 'Upload saved to storage but metadata failed');
  return saved.asset;
}

// PUT the bytes directly to the presigned MinIO URL with upload-progress.
//
// TICKET-476: this used to be a second hand-rolled XHR PUT whose whole error
// vocabulary was "Upload failed (network error)" with no spine emit. When CSP
// blocked the storage origin, that message was all anyone had — and no template
// asset had EVER uploaded. Delegating to the shared putToPresignedUrl means one
// upload failure class, one diagnosis, one telemetry step for every surface.
// It reports (loaded, total); this API's onProgress takes a 0..1 fraction.
function putWithProgress(url, file, onProgress) {
  return putToPresignedUrl({
    url,
    file,
    step: 'quote_template_asset.put',
    onProgress: onProgress ? (loaded, total) => onProgress(total ? loaded / total : 0) : undefined,
  });
}

// GET quotes.get_settings — returns { settings } | { settings: null }.
// Reads the single quoteflow_settings row (logo, IBAN, TOS text, etc.).
export async function getQuoteflowSettings() {
  const r = await apiFetch(D('quotes.get_settings'));
  return r?.settings ?? null;
}

// ── Customer comments (WS2-4) ───────────────────────────────────────────────
// GET quotes.list_comments — returns { success, comments: [...] } for a quote.
export function listQuoteComments({ quote_id }) {
  return apiFetch(`${D('quotes.list_comments')}&quote_id=${encodeURIComponent(quote_id)}`);
}

// POST quotes.resolve_comment — marks a comment resolved.
// Returns { success, comment: { id, resolved_at, resolved_by } }.
export function resolveQuoteComment({ comment_id }) {
  return apiFetch(D('quotes.resolve_comment'), { method: 'POST', body: JSON.stringify({ comment_id }) });
}

// ── Internal review-comment thread (TICKET-264 Gap 1, approver-gated) ────────
// POST quotes.add_review_comment — an approver adds an internal review note.
// Returns { success, comment: { id, quote_id, comment, author, comment_thread_kind, created_at } }.
export function addQuoteReviewComment({ quote_id, comment }) {
  return apiFetch(D('quotes.add_review_comment'), { method: 'POST', body: JSON.stringify({ quote_id, comment }) });
}
// GET quotes.list_review_comments — the internal review-note thread for a quote.
export function listQuoteReviewComments({ quote_id }) {
  return apiFetch(`${D('quotes.list_review_comments')}&quote_id=${encodeURIComponent(quote_id)}`);
}

// POST quotes.send_link_email — (re-)send the signed PDF to the customer + CEO.
// `to` is optional: omit to use the flagged recipient from the deal contacts.
// Returns { success, sent: boolean, reason? }.
export function sendQuoteLinkEmail({ quote_id, to } = {}) {
  return apiFetch(D('quotes.send_link_email'), { method: 'POST', body: JSON.stringify({ quote_id, to }) });
}

// ── Engagement analytics (CT-3) ─────────────────────────────────────────────
// GET quotes.engagement_summary — QUOTE_READ role-gated server-side rollup of
// the pre-auth quotes.public_engage event stream for one quote. Returns
// { total_views, last_viewed_at, total_dwell_ms, top_dwell_sections:
// [{section,ms}], options_toggled_count, funnel:{sent_at,first_view_at,
// configured_at,signed_at} } | null on failure/403/404.
export async function getQuoteEngagementSummary(quoteId) {
  const r = await apiFetch(`${D('quotes.engagement_summary')}&quote_id=${encodeURIComponent(quoteId)}`);
  return r?.success ? r.summary : null;
}

// ── Copilot efficiency (TICKET-1518) ─────────────────────────────────────────
// GET quotes.copilot_efficiency -- turns-to-quote rollup: { window_days, quotes, median_turns_to_send,
// median_turns_total, avg_avoidable_turns, median_minutes_to_send, trend:[{week,quotes,median_turns_to_send,
// avg_avoidable_turns}], gap_categories:[{category,count}] } | null on failure/403.
export async function getCopilotEfficiency() {
  const r = await apiFetch(D('quotes.copilot_efficiency'));
  return r?.success ? r.efficiency : null;
}

// GET quotes.line_sources&evidence=1 -- past-quote price evidence for ONE line (TICKET-1518). One guarded embedding per
// call, so the line price card only calls it on a click. Returns { this_class, class_known, suggested_per_piece_eur,
// based_on, needed, groups:{same_deal,same_boat,same_class,other_class:[row]} } | null (403 / paused / failure).
export async function getLinePriceEvidence(lineId) {
  const r = await apiFetch(`${D('quotes.line_sources')}&evidence=1&line_id=${encodeURIComponent(lineId)}`);
  return r?.success ? r.evidence : null;
}

// GET quotes.list&similar_deal_id -- top 3 similar past quotes for a deal (TICKET-1518 S5). One guarded embedding per call,
// so the picker calls it only when opened. Returns { this_class, class_known, quotes:[{quote_id, quote_number, boat, class,
// date, tier, matched_lines, examples, score}] } | null (403 / paused / failure).
export async function getSimilarQuotes(dealId) {
  const r = await apiFetch(`${D('quotes.list')}&similar_deal_id=${encodeURIComponent(dealId)}`);
  return r?.success ? r : null;
}
